import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Alert,
  Button,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, pricingAPI, powerAddonsAPI, regionsAPI, powerAddonOptionsAPI } from '../../../services/api';
import { Provider, PowerAddon, PowerAddonOption, Region, PowerAddonOptionPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { monthlyCost?: number; isAvailable?: boolean };
}

const PowerAddonOptionPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [powerAddons, setPowerAddons] = useState<PowerAddon[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [pricing, setPricing] = useState<PowerAddonOptionPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<PriceChangeMap>({});
  const [powerAddonOptions, setPowerAddonOptions] = useState<Record<number, PowerAddonOption[]>>({});

  const fetchInitialData = async () => {
    try {
      const [providersRes, powerAddonsRes] = await Promise.all([
        providersAPI.getAll(),
        powerAddonsAPI.getAll(),
      ]);
      const activeProviders = providersRes.data.filter((p: Provider) => p.IsActive);
      const activePowerAddons = powerAddonsRes.data.filter((a: PowerAddon) => a.IsActive && a.HasOptions);

      setProviders(activeProviders);
      setPowerAddons(activePowerAddons);

      // Fetch options for each power addon
      const optionsMap: Record<number, PowerAddonOption[]> = {};
      for (const powerAddon of activePowerAddons) {
        try {
          const optionsRes = await powerAddonOptionsAPI.getByPowerAddon(powerAddon.PowerAddonId);
          optionsMap[powerAddon.PowerAddonId] = optionsRes.data.filter((o: PowerAddonOption) => o.IsActive);
        } catch {
          optionsMap[powerAddon.PowerAddonId] = [];
        }
      }
      setPowerAddonOptions(optionsMap);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;

    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        pricingAPI.getPowerAddonOptionPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);

      setPricing(pricingRes.data);
      setRegions(regionsRes.data.filter((r: Region) => r.IsActive));
      setChanges({});
    } catch {
      setError('Failed to load pricing');
    }
  }, [providers, selectedProvider]);

  useEffect(() => {
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (providers.length > 0) {
      fetchPricingData();
    }
  }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number, field: string, value: number | boolean) => {
    setChanges({
      ...changes,
      [pricingId]: {
        ...changes[pricingId],
        [field]: value,
      },
    });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const originalPricing = pricing.find(p => p.PowerAddonOptionPricingId === parseInt(pricingId));
        const mergedData = {
          monthlyCost: changedFields.monthlyCost !== undefined
            ? changedFields.monthlyCost
            : originalPricing?.MonthlyCost ?? 0,
          isAvailable: changedFields.isAvailable !== undefined
            ? changedFields.isAvailable
            : originalPricing?.IsAvailable ?? 0,
        };
        return pricingAPI.updatePowerAddonOptionPrice(pricingId, mergedData);
      });

      await Promise.all(updates);
      setSuccess(`Updated ${Object.keys(changes).length} pricing entries`);
      setChanges({});
      fetchPricingData();

      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to update prices');
    }
  };

  const getPriceValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.monthlyCost !== undefined
      ? changes[pricingId]!.monthlyCost!
      : defaultValue;
  };

  const getAvailableValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.isAvailable !== undefined
      ? changes[pricingId]!.isAvailable!
      : !!defaultValue;
  };

  const handleToggleAll = (entries: PowerAddonOptionPricingRow[], value: boolean) => {
    const newChanges: PriceChangeMap = { ...changes };
    entries.forEach(entry => {
      if (entry) {
        newChanges[entry.PowerAddonOptionPricingId] = {
          ...newChanges[entry.PowerAddonOptionPricingId],
          isAvailable: value,
        };
      }
    });
    setChanges(newChanges);
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Power Addon Option Pricing</Typography>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          startIcon={<SaveIcon fontSize="small" />}
          onClick={handleSaveChanges}
          disabled={Object.keys(changes).length === 0}
        >
          Save Changes ({Object.keys(changes).length})
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={selectedProvider}
          onChange={(_e, newValue: number) => setSelectedProvider(newValue)}
          variant="fullWidth"
        >
          {providers.map((provider) => (
            <Tab key={provider.ProviderId} label={provider.ProviderName} />
          ))}
        </Tabs>
      </Paper>

      {powerAddons.map((powerAddon) => {
        const options = powerAddonOptions[powerAddon.PowerAddonId] || [];

        if (options.length === 0) {
          return null;
        }

        return (
          <Paper key={powerAddon.PowerAddonId} sx={{ mb: 2, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {powerAddon.PowerAddonName}
            </Typography>
            {powerAddon.Description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {powerAddon.Description}
              </Typography>
            )}

            {options.map((option) => {
              const optionPricing = pricing.filter(p => p.PowerAddonOptionId === option.PowerAddonOptionId);

              return (
                <Box key={option.PowerAddonOptionId} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      {option.OptionName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="outlined" color="success" onClick={() => handleToggleAll(optionPricing, true)}>Enable All</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => handleToggleAll(optionPricing, false)}>Disable All</Button>
                    </Box>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Provider</TableCell>
                          {regions.map((region) => (
                            <TableCell key={region.RegionId} align="center">
                              {region.RegionName}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Monthly Cost ($)</TableCell>
                          {regions.map((region) => {
                            const priceEntry = optionPricing.find(p => p.RegionId === region.RegionId);
                            if (!priceEntry) return <TableCell key={region.RegionId} align="center">-</TableCell>;

                            return (
                              <TableCell key={region.RegionId} align="center">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={getPriceValue(priceEntry.PowerAddonOptionPricingId, priceEntry.MonthlyCost)}
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    handlePriceChange(priceEntry.PowerAddonOptionPricingId, 'monthlyCost', value);
                                  }}
                                  inputProps={{ step: 0.01, min: 0 }}
                                  sx={{
                                    width: 100,
                                    '& .MuiInputBase-input': {
                                      fontSize: '0.875rem',
                                      py: 0.5,
                                      px: 1,
                                    },
                                  }}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        <TableRow>
                          <TableCell>Enabled</TableCell>
                          {regions.map((region) => {
                            const priceEntry = optionPricing.find(p => p.RegionId === region.RegionId);
                            if (!priceEntry) return <TableCell key={region.RegionId} align="center">-</TableCell>;

                            return (
                              <TableCell key={region.RegionId} align="center">
                                <Checkbox
                                  checked={getAvailableValue(priceEntry.PowerAddonOptionPricingId, priceEntry.IsAvailable)}
                                  onChange={(e) =>
                                    handlePriceChange(priceEntry.PowerAddonOptionPricingId, 'isAvailable', e.target.checked)
                                  }
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })}
          </Paper>
        );
      })}
    </Box>
  );
};

export default PowerAddonOptionPricing;
